import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { isMediaArtifactFile } from "@repo/shared";
import { ImageOff } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DocumentReadScopePopover } from "@/features/documents/components/document-read-scope-popover";
import { TranscriptDialog } from "@/features/documents/components/transcript-dialog";
import { useAnswerReadScope } from "@/features/documents/hooks/use-answer-read-scope";
import { useDownloadTranscript } from "@/features/documents/hooks/use-download-transcript";
import { useObservedMutation } from "@/hooks/use-observed-convex";
import { cn } from "@/lib/utils";
import { useRetryArtifactProcessingJob, useThreadAttachmentProcessing } from "../hooks";
import {
  resolveAttachmentChipStatus,
  resolveAttachmentOpenTranscript,
  resolveAttachmentReadScopeQuestion,
} from "./attachment-chip-status";
import { AttachmentDocumentChip } from "./attachment-document-chip";
import {
  AttachmentPreviewDialog,
  type AttachmentPreviewDialogItem,
} from "./attachment-preview-dialog";

export type AssistantMessageAttachmentPart = {
  type: "attachment";
  attachmentId: string;
  artifactId?: string;
  kind: "image" | "document";
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  width?: number;
  height?: number;
  url: string | null;
  thumbnailUrl: string | null;
};

export function AssistantMessageAttachments({
  attachments,
  compact,
  projectId,
  threadId,
}: {
  attachments: AssistantMessageAttachmentPart[];
  compact?: boolean;
  projectId: Id<"projects">;
  threadId: string;
}) {
  const [preview, setPreview] = useState<AttachmentPreviewDialogItem | null>(null);
  const [promotingArtifactId, setPromotingArtifactId] = useState<string | null>(null);
  const [openQuestionArtifactId, setOpenQuestionArtifactId] = useState<string | null>(null);
  // The recording whose transcript is being read, or `null`. The viewer subscribes to the
  // recording's turns and speaker names, so it is mounted only while one is open.
  const [transcriptTarget, setTranscriptTarget] = useState<{
    artifactId: string;
    name: string;
  } | null>(null);
  const { downloadTranscript, isDownloadingTranscript } = useDownloadTranscript(projectId);
  // The same mutation the Documents row and the element Attachments tab answer with: a parked
  // file's question belongs to the file, not to the screen it was noticed on.
  const { answerReadScope, isAnswering } = useAnswerReadScope(projectId);
  const promote = useObservedMutation(api.artifacts.promoteThreadAttachmentToProjectKnowledge, {
    feature: "claudia.attachments",
    operation: "promote_to_project_knowledge",
  });
  const retryProcessingJob = useRetryArtifactProcessingJob();
  const imageAttachments = attachments.filter((attachment) => attachment.kind === "image");
  const documentAttachments = attachments.filter((attachment) => attachment.kind === "document");
  const processing = useThreadAttachmentProcessing(
    projectId,
    threadId,
    documentAttachments.length > 0
  );
  if (imageAttachments.length === 0 && documentAttachments.length === 0) {
    return null;
  }

  // Cap thumbnails at 120px (96px in the narrow panel) on both axes; the image keeps its aspect
  // ratio and is shown whole (no crop), so the bordered box hugs the actual image.
  const imageCapClass = compact ? "max-h-24 max-w-24" : "max-h-[120px] max-w-[120px]";
  const brokenBoxClass = compact ? "size-24" : "size-[120px]";

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {attachments.map((attachment) => {
          if (attachment.kind === "document") {
            const processingFile = processing?.files.find(
              (file) => file.artifactId === attachment.artifactId
            );
            const question = resolveAttachmentReadScopeQuestion(
              processingFile,
              attachment,
              Date.now()
            );
            // A recording has no URL to render and never gets one here: the object is signed on
            // the press, elsewhere. What it offers instead is its words, once there are any.
            const isRecording = isMediaArtifactFile(attachment.fileName, attachment.mimeType);
            const transcriptArtifactId =
              isRecording &&
              attachment.artifactId &&
              resolveAttachmentOpenTranscript(processingFile)
                ? attachment.artifactId
                : null;
            return (
              <AttachmentDocumentChip
                key={attachment.attachmentId}
                fileName={attachment.fileName}
                mimeType={attachment.mimeType}
                fileSizeBytes={attachment.fileSizeBytes}
                status={resolveAttachmentChipStatus(processingFile?.status)}
                error={
                  processingFile?.status === "failed" ? "Couldn't read this document" : undefined
                }
                onRetry={
                  processingFile?.status === "failed" && processingFile.jobId
                    ? async () => {
                        try {
                          await retryProcessingJob({
                            jobId: processingFile.jobId as Id<"artifactProcessingJobs">,
                          });
                        } catch (error) {
                          toast.error(
                            error instanceof Error ? error.message : "Couldn't retry this document."
                          );
                        }
                      }
                    : undefined
                }
                compact={compact}
                onOpen={
                  isRecording
                    ? transcriptArtifactId
                      ? () =>
                          setTranscriptTarget({
                            artifactId: transcriptArtifactId,
                            name: attachment.fileName,
                          })
                      : undefined
                    : attachment.url
                      ? () => window.open(attachment.url!, "_blank", "noopener,noreferrer")
                      : undefined
                }
                isPopoverOpen={question ? openQuestionArtifactId === question.artifactId : false}
                onPopoverOpenChange={(open) =>
                  setOpenQuestionArtifactId(open && question ? question.artifactId : null)
                }
                popover={
                  question ? (
                    <DocumentReadScopePopover
                      payload={question}
                      isPending={isAnswering(question.artifactId)}
                      // No "Delete upload" here, deliberately: the file is part of a message
                      // that has already been sent, and the chip beside it would then name
                      // something nobody can open. Removing it is the Documents screen's job.
                      onSubmit={async (answer) => {
                        const outcome = await answerReadScope({
                          artifactId: question.artifactId,
                          name: question.title,
                          answer,
                        });
                        if (outcome.ok) setOpenQuestionArtifactId(null);
                        return outcome;
                      }}
                    />
                  ) : undefined
                }
                // Dimmed means "the bytes behind this are gone", which is a storage-backed
                // file's condition alone. A recording's row carries no URL by design.
                className={!attachment.url && !isRecording ? "opacity-70" : undefined}
                onAddToProjectKnowledge={
                  attachment.artifactId
                    ? async () => {
                        setPromotingArtifactId(attachment.artifactId!);
                        try {
                          await promote({
                            projectId,
                            artifactId: attachment.artifactId as Id<"artifacts">,
                            threadId,
                          });
                          toast.success("Added to project knowledge.");
                        } catch (error) {
                          toast.error(
                            error instanceof Error ? error.message : "Couldn't add this document."
                          );
                        } finally {
                          setPromotingArtifactId(null);
                        }
                      }
                    : undefined
                }
                isAddingToProjectKnowledge={promotingArtifactId === attachment.artifactId}
              />
            );
          }

          const thumbnailUrl = attachment.thumbnailUrl ?? attachment.url;
          return thumbnailUrl ? (
            <button
              key={attachment.attachmentId}
              type="button"
              onClick={() =>
                attachment.url
                  ? setPreview({ fileName: attachment.fileName, url: attachment.url })
                  : undefined
              }
              className="group relative inline-flex overflow-hidden border border-border bg-muted text-left"
              disabled={!attachment.url}
            >
              <img
                src={thumbnailUrl}
                alt={attachment.fileName}
                className={cn("block h-auto w-auto object-contain", imageCapClass)}
                loading="lazy"
              />
              <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1.5 py-1 text-[11px] text-white">
                {attachment.fileName}
              </span>
            </button>
          ) : (
            <div
              key={attachment.attachmentId}
              className={cn(
                "flex flex-col items-center justify-center gap-1 border border-border bg-muted px-2 text-center text-[11px] text-muted-foreground",
                brokenBoxClass
              )}
            >
              <ImageOff className="size-4" />
              <span className="line-clamp-2">{attachment.fileName}</span>
            </div>
          );
        })}
      </div>
      <AttachmentPreviewDialog
        attachment={preview}
        onOpenChange={(open) => !open && setPreview(null)}
      />
      {/* Mounted only while a transcript is being read, on the terms the Documents row and the
          evidence popover mount it on: the viewer subscribes to the recording's speaker names
          and binds their rename, and a message nobody opened a transcript from holds neither. */}
      {transcriptTarget === null ? null : (
        <TranscriptDialog
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setTranscriptTarget(null);
          }}
          projectId={projectId}
          artifactId={transcriptTarget.artifactId}
          name={transcriptTarget.name}
          onDownload={() => {
            void downloadTranscript(transcriptTarget.artifactId, transcriptTarget.name);
          }}
          isDownloading={isDownloadingTranscript(transcriptTarget.artifactId)}
        />
      )}
    </>
  );
}
